// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { Button, Markdown, StatusMessage, useTranslate, copyToClipboard } from '@plesk/ui-library';
import type { Tokens } from 'marked';
import React, { useEffect, useRef } from 'react';
import { Answer } from '../../CopilotContext';
import { addToast } from 'helpers/toasts';
import { escapeHtml } from 'jsw';

type MessageItemProps = {
    message: Answer;
    onFeedback: (id: Answer['id'], value: Answer['feedback']) => void;
    feedbackDisabled?: boolean;
};

const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) {
        return text;
    }
    return `${text.slice(0, maxLength)}...`;
};

const getHtmlFromLinkToken = (t: Tokens.Link): string => {
    try {
        const linkUrl = new URL(t.href);
        linkUrl.searchParams.set('utm_source', 'pleskobsidian');
        linkUrl.searchParams.set('utm_medium', 'ai_assistant');
        const currentUrl = new URL(window.location.href);
        const isSameDomain = linkUrl.hostname === currentUrl.hostname;
        const text = truncateText(t.text, 50);
        const title = t.title || (t.text !== text ? t.text : null) || null;

        return `<a href="${linkUrl.toString()}"${title ? ` data-title="${title}"` : ''} ${isSameDomain ? '' : 'target="_blank" rel="noopener noreferrer"'}>${text}</a>`;
    } catch {
        return `<span>${t.text || ''}</span>`;
    }
};

const MessageItem: React.FC<MessageItemProps> = ({ message, onFeedback, feedbackDisabled }) => {
    const translate = useTranslate('components.copilot');

    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const copyButton = target.closest('.code-copy-icon');
            if (copyButton) {
                const code = copyButton.closest('.pre-code-wrapper')?.querySelector('code')?.textContent;
                if (code) {
                    try {
                        copyToClipboard(code);
                        addToast({
                            intent: 'success',
                            message: translate('copy-snippet-success') as string,
                        });
                    } catch {
                        addToast({
                            intent: 'danger',
                            message: translate('copy-snippet-error') as string,
                        });
                    }
                }
            }
        };
        const node = contentRef.current;
        node?.addEventListener('click', handler);
        return () => node?.removeEventListener('click', handler);
    }, [translate]);

    const feedbackClass = message.feedback === 'positive' ? 'feedback-up' : message.feedback === 'negative' ? 'feedback-down' : '';

    return (
        <div
            key={message.id}
            className={`plesk-copilot-msg-row ${message.role === 'user' ? 'right' : 'left'}`}
        >
            {message.role === 'assistant' ? (
                <div className="plesk-copilot-assistant-card">
                    <div className="assistant-content" ref={contentRef}>
                        {message.error ? (
                            <StatusMessage intent="danger">{message.error}</StatusMessage>
                        ) : (
                            <Markdown parserOptions={{
                                extensions: {
                                    renderers: {
                                        link: token => getHtmlFromLinkToken(token as Tokens.Link),
                                        code: token => '<div class="pre-code-wrapper"><pre>' +
                                            // eslint-disable-next-line max-len
                                            `<button class="code-copy-icon pul-button pul-button--ghost pul-button--empty" type="button" aria-label="${translate('copy-snippet-label')}" title="${translate('copy-snippet-label')}">` +
                                            '<span class="pul-button__inner">' +
                                            '<span class="pul-icon pul-icon--size-16 pul-button__icon" aria-hidden="true">' +
                                            '<svg focusable="false"><use href="/cp/theme/icons/24/colored-icons-sprite.svg#copy"></use></svg></span>' +
                                            `</span></button><code>${escapeHtml(token.text)}</code></pre></div>`,
                                    },
                                    childTokens: {},
                                },

                            }}
                            >
                                {message.content ?? ''}
                            </Markdown>
                        )}
                    </div>
                    <div className="plesk-copilot-assistant-actions">
                        {!message.error
                            && message.chat_id
                            && message.message_id !== undefined
                            && message.message_id !== null
                            && message.able_to_answer !== false
                            && !message.streaming && (
                            <div className={`plesk-copilot-feedback-buttons ${feedbackClass}`}>
                                <Button
                                    icon={
                                        <span className="pul-icon pul-icon--size-16">
                                            <svg focusable="false"><use href="/cp/theme/icons/24/colored-icons-sprite.svg#thumbs-up" /></svg>
                                        </span>
                                    }
                                    ghost
                                    tooltip={translate('user-feedback-helpful')}
                                    onClick={() => onFeedback(message.id, 'positive')}
                                    tooltipAsLabel
                                    aria-pressed={message.feedback === 'positive'}
                                    disabled={feedbackDisabled}
                                />
                                <Button
                                    icon={
                                        <span className="pul-icon pul-icon--size-16">
                                            <svg focusable="false"><use href="/cp/theme/icons/24/colored-icons-sprite.svg#thumbs-down" /></svg>
                                        </span>
                                    }
                                    ghost
                                    tooltip={translate('user-feedback-not-helpful')}
                                    onClick={() => onFeedback(message.id, 'negative')}
                                    tooltipAsLabel
                                    aria-pressed={message.feedback === 'negative'}
                                    disabled={feedbackDisabled}
                                />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="user-chip">
                    <span>{message.content}</span>
                </div>
            )}
        </div>
    );
};

export default MessageItem;
